import type { WorkOrderSeasonData } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { getNumberFormatter } from 'pages/Project/Common/utils';

import { useMemo } from 'react';

import { WorkOrderOtherSeasonPlanCell } from '../WorkOrderOtherEditableCells';

export const useOthersSeasonsColumns = ({
    seasons,
    workOrderStatus,
    hasInProgress,
    costRounding,
}: {
    seasons: WorkOrderSeasonData[];
    workOrderStatus?: WorkOrderStatusEnum;
    hasInProgress?: boolean;
    costRounding: number;
}) => {
    const withFactAndRemainderColumns =
        workOrderStatus &&
        ([WorkOrderStatusEnum.InProgress, WorkOrderStatusEnum.Completed].includes(workOrderStatus) ||
            (workOrderStatus === WorkOrderStatusEnum.Delayed && hasInProgress));

    return useMemo(
        () =>
            seasons?.map(({ id, year }, index: number) => {
                const isOddSeason = !(index % 2);
                const cellClass = isOddSeason ? 'oddSeason' : 'evenSeason';

                const factColumns = withFactAndRemainderColumns
                    ? [
                          {
                              id,
                              seasonId: id,
                              headerName: `Сезон ${year}, Выполнено`,
                              headerClass: `${cellClass} flexStart`,
                              marryChildren: true,

                              children: [
                                  {
                                      headerName: 'Объем',
                                      field: `seasonVolume_${id}`,
                                      cellClass: `ag-right-aligned-cell ${cellClass}`,
                                      headerClass: cellClass,
                                      width: 112,
                                  },
                                  {
                                      headerName: 'Стоимость, руб. без НДС',
                                      field: `seasonCompletedCost_${id}`,
                                      cellClass: `ag-right-aligned-cell ${cellClass}`,
                                      headerClass: cellClass,
                                      width: 130,
                                      valueGetter: ({ data }: { data: any }) =>
                                          (data as any)[`seasonCompletedCost_${id}`] ?? '',
                                      valueFormatter: getNumberFormatter(costRounding || 0),
                                  },
                              ],
                          },
                          {
                              id,
                              seasonId: id,
                              field: `Сезон ${year}, Остаток`,
                              headerName: `Сезон ${year}, Остаток`,
                              headerClass: `${cellClass} flexStart`,
                              marryChildren: true,

                              children: [
                                  {
                                      headerName: 'Объем',
                                      field: `seasonVolume_${id}`,
                                      cellClass: `ag-right-aligned-cell ${cellClass}`,
                                      headerClass: cellClass,
                                      width: 112,
                                  },
                                  {
                                      headerName: 'Стоимость, руб. без НДС',
                                      field: `seasonRemainderCost_${id}`,
                                      cellClass: `ag-right-aligned-cell ${cellClass}`,
                                      headerClass: cellClass,
                                      width: 130,
                                      valueGetter: ({ data }: { data: any }) =>
                                          (data as any)[`seasonRemainderCost_${id}`] ?? '',
                                      valueFormatter: getNumberFormatter(costRounding || 0),
                                  },
                              ],
                          },
                      ]
                    : [];

                return [
                    {
                        id,
                        seasonId: id,

                        field: `Сезон ${year}`,
                        headerName: `Сезон ${year}${withFactAndRemainderColumns ? ', План' : ''}`,
                        headerClass: `${cellClass} flexStart`,
                        marryChildren: true,

                        children: [
                            {
                                headerName: 'Объем',
                                field: `seasonVolume_${id}`,
                                cellClass: `ag-right-aligned-cell ${cellClass}`,
                                headerClass: cellClass,
                                width: 112,
                            },
                            {
                                headerName: 'Стоимость, руб. без НДС',
                                field: `seasonValue_${id}`,
                                cellClass: `ag-right-aligned-cell ${cellClass}`,
                                headerClass: cellClass,
                                width: 130,
                                valueGetter: ({ data }: { data: any }) => ({
                                    value: (data as any)[`seasonValue_${id}`],
                                }),
                                valueFormatter: getNumberFormatter(costRounding || 0),
                                cellRenderer: WorkOrderOtherSeasonPlanCell,
                                cellRendererParams: {
                                    decimal: costRounding,
                                    seasonId: id,
                                },
                            },
                        ],
                    },
                    ...factColumns,
                ];
            }),
        [seasons, withFactAndRemainderColumns, costRounding],
    );
};
